// Three-gate dot indicator for the milestone table — Delivered → Accepted → Paid. Replaces StepperCell internals.

import { clsx } from "clsx";
import type { Milestone } from "../../../../types";
import {
  deriveProgressGates,
  type DeliveredGate,
  type AcceptedGate,
  type PaidGate,
} from "../../../../utils/deriveProgressGates";
import { formatDayMonthYear } from "../../../../utils/formatShortDate";
import AmberHalfFillDot from "../shared/AmberHalfFillDot";

const DOT_BASE = "h-2.5 w-2.5 rounded-full border";

const DELIVERED_CLASS: Record<DeliveredGate, string> = {
  not_due_yet: "border-slate-300 bg-slate-100 dark:border-slate-600 dark:bg-slate-800",
  on_time: "border-emerald-500 bg-emerald-500",
  delivered_late: "border-amber-500 bg-amber-500",
  late_undelivered: "border-red-500 bg-red-500",
};

const ACCEPTED_FILLED_CLASS = "border-emerald-500 bg-emerald-500";
// `partial` no longer uses an aClass — it renders through
// AmberHalfFillDot (the shared primitive). Keep filled + empty as
// inline classes for the two non-partial branches.
const ACCEPTED_EMPTY_CLASS =
  "border-slate-300 bg-slate-100 dark:border-slate-600 dark:bg-slate-800";

const PAID_FILLED_CLASS = "border-emerald-500 bg-emerald-500";
const PAID_EMPTY_CLASS =
  "border-slate-300 bg-slate-100 dark:border-slate-600 dark:bg-slate-800";

const CONNECTOR = (
  <span className="h-px w-3 bg-slate-300 dark:bg-slate-600" aria-hidden="true" />
);

interface ProgressDotsProps {
  milestone: Milestone;
  /** Test injection. Defaults to today via deriveProgressGates. */
  today?: string;
}

function deliveredLabel(gate: DeliveredGate): string {
  if (gate === "late_undelivered") return "Late — not yet delivered";
  if (gate === "delivered_late") return "Delivered (late)";
  return "Delivered";
}

function acceptedLabel(gate: AcceptedGate): string {
  if (gate === "partial") return "Partially accepted";
  return "Accepted";
}

const PAID_LABEL = "Paid";

function deliveredTooltip(milestone: Milestone, gate: DeliveredGate): string {
  const planned = milestone.plannedDate
    ? formatDayMonthYear(milestone.plannedDate)
    : "no planned date";
  if (gate === "late_undelivered") {
    return `Late — planned ${planned}, not yet delivered`;
  }
  if (milestone.deliveredAt) {
    const suffix = gate === "delivered_late" ? " (late)" : "";
    return `Delivered ${formatDayMonthYear(milestone.deliveredAt)}${suffix}`;
  }
  return `Awaiting delivery (planned ${planned})`;
}

function acceptedTooltip(milestone: Milestone, gate: AcceptedGate): string {
  if (gate === "fully_accepted" && milestone.acceptedAt) {
    return `Accepted ${formatDayMonthYear(milestone.acceptedAt)}`;
  }
  if (gate === "partial" && milestone.partiallyAcceptedAt) {
    return `Partially accepted ${formatDayMonthYear(milestone.partiallyAcceptedAt)} — awaiting full acceptance`;
  }
  return "Awaiting acceptance";
}

function paidTooltip(milestone: Milestone, gate: PaidGate): string {
  if (gate === "paid" && milestone.paidAt) {
    return `Paid ${formatDayMonthYear(milestone.paidAt)}`;
  }
  return "Awaiting payment";
}

export default function ProgressDots({ milestone, today }: ProgressDotsProps) {
  const gates = deriveProgressGates(milestone, today);

  const dLabel = deliveredLabel(gates.delivered);
  const aLabel = acceptedLabel(gates.accepted);
  const pLabel = PAID_LABEL;

  // partial is rendered via AmberHalfFillDot — aClass only covers the
  // two non-partial branches.
  const aClass =
    gates.accepted === "fully_accepted"
      ? ACCEPTED_FILLED_CLASS
      : ACCEPTED_EMPTY_CLASS;

  const pClass = gates.paid === "paid" ? PAID_FILLED_CLASS : PAID_EMPTY_CLASS;

  return (
    <div className="flex items-center gap-1">
      <span
        data-testid="step-dot-delivered"
        data-gate-state={gates.delivered}
        aria-label={dLabel}
        title={deliveredTooltip(milestone, gates.delivered)}
        role="img"
        className={clsx(DOT_BASE, DELIVERED_CLASS[gates.delivered])}
      />
      {CONNECTOR}
      {gates.accepted === "partial" ? (
        <AmberHalfFillDot
          size="sm"
          ariaLabel={aLabel}
          title={acceptedTooltip(milestone, gates.accepted)}
          outerTestId="step-dot-accepted"
          outerDataAttrs={{ "data-gate-state": gates.accepted }}
          testId="partial-dot-half-fill"
        />
      ) : (
        <span
          data-testid="step-dot-accepted"
          data-gate-state={gates.accepted}
          aria-label={aLabel}
          title={acceptedTooltip(milestone, gates.accepted)}
          role="img"
          className={clsx(DOT_BASE, aClass)}
        />
      )}
      {CONNECTOR}
      <span
        data-testid="step-dot-paid"
        data-gate-state={gates.paid}
        aria-label={pLabel}
        title={paidTooltip(milestone, gates.paid)}
        role="img"
        className={clsx(DOT_BASE, pClass)}
      />
    </div>
  );
}
